/**
 * Helpers for `/snippet` embeds — block-level references into other docs.
 */

import { flattenBlocks } from './blockUtils';
import { htmlToText } from './caretUtils';

/** Layout / self-reference types that cannot be embedded. */
const UNPICKABLE_TYPES = new Set(['row', 'column', 'choice-path', 'snippet']);

export const SNIPPET_TYPE_LABELS = {
  text: 'Text',
  heading: 'Heading',
  checkbox: 'Checkbox',
  bullet: 'Bullet',
  number: 'Numbered',
  callout: 'Section',
  choice: 'Choice',
  toggle: 'Toggle',
  code: 'Code',
  table: 'Table',
  image: 'Image',
  video: 'Video',
  divider: 'Divider',
  variables: 'Variables',
  'linked-variable-row': 'Linked variable row',
  'file-upload': 'File upload',
};

export const snippetTypeLabel = (block) => {
  if (!block) return 'Block';
  if (block.type === 'callout' && block.props?.variant === 'quote') return 'Quote';
  if (block.type === 'heading') {
    const level = block.props?.level || 1;
    return `Heading ${level}`;
  }
  return SNIPPET_TYPE_LABELS[block.type] || block.type || 'Block';
};

/** One-line plain preview for cards / picker rows. */
export const snippetPreviewText = (block) => {
  if (!block) return '';
  switch (block.type) {
    case 'image':
      return block.props?.fileName || 'Image';
    case 'video':
      return (
        block.props?.fileName ||
        block.props?.originalUrl ||
        (block.props?.provider ? `${block.props.provider} video` : 'Video')
      );
    case 'divider':
      return '—';
    case 'variables':
      return 'Variables table';
    case 'linked-variable-row':
      return block.props?.variableName
        ? `{{${block.props.variableName}}}`
        : 'Linked variable row';
    case 'file-upload': {
      const label = htmlToText(block.content || '').trim();
      return label || 'File upload';
    }
    case 'table': {
      const headers = block.props?.tableData?.headers || [];
      const text = headers
        .map((h) =>
          (Array.isArray(h)
            ? h.map((b) => htmlToText(b.content || '').trim()).filter(Boolean).join(' ')
            : htmlToText(h || '').trim())
        )
        .filter(Boolean)
        .join(' · ');
      return text || 'Table';
    }
    case 'code': {
      const text = htmlToText(block.content || '')
        .split('\n')
        .map((l) => l.trim())
        .find(Boolean);
      return text || 'Code';
    }
    default: {
      const text = htmlToText(block.content || '').trim().replace(/\s+/g, ' ');
      if (text) return text;
      // Callout / toggle with only children
      if (block.children?.length) {
        const child = block.children.find((c) => htmlToText(c.content || '').trim());
        if (child) return htmlToText(child.content || '').trim().replace(/\s+/g, ' ');
      }
      return '(empty)';
    }
  }
};

/** Flatten a doc body into pickable embed targets (document order). */
export const listPickableSnippetBlocks = (body) =>
  flattenBlocks(body || []).filter((b) => !UNPICKABLE_TYPES.has(b.type));

export const isPickableSnippetBlock = (block) =>
  !!block && !UNPICKABLE_TYPES.has(block.type);

/**
 * Normalize snippet props to an ordered list of source block ids.
 * Supports legacy `sourceBlockId` and multi-block `sourceBlockIds`.
 */
export const normalizeSourceBlockIds = (props = {}) => {
  if (Array.isArray(props.sourceBlockIds) && props.sourceBlockIds.length) {
    return props.sourceBlockIds.filter(Boolean);
  }
  if (props.sourceBlockId) return [props.sourceBlockId];
  return [];
};

export const truncatePreview = (text, max = 120) => {
  const s = (text || '').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
};
